import { useMemo, useState, type ReactNode } from 'react'
import { t } from '@/i18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  bulkConfirmMatches,
  bulkRejectMatches,
  confirmManualMatch,
  confirmMatch,
  getReconBoard,
  rejectMatch,
  resolveException,
  runReconciliation,
  type CandidateOut,
  type MatchOut,
  type ReconFactor,
  type ReconOrderOut,
  type ReconTxOut,
} from '@/lib/api'
import { formatDateTime, formatMoney } from '@/lib/format'

function sideLabel(side: 'buy' | 'sell'): string {
  return side === 'sell' ? t('common.sell') : t('common.buy')
}

function typeLabel(kind: string): string {
  if (kind === '1:N') return t('rec.type1n')
  if (kind === 'N:1') return t('rec.typeN1')
  return kind
}

const SEVERITY_CLASS: Record<string, string> = {
  critical: 'text-loss',
  high: 'text-pending',
  medium: 'text-slate-200',
  low: 'text-slate-400',
  info: 'text-slate-500',
}

export function Reconciliation() {
  const queryClient = useQueryClient()
  const board = useQuery({ queryKey: ['reconciliation'], queryFn: getReconBoard })
  const [selectedOrders, setSelectedOrders] = useState<string[]>([])
  const [selectedTxs, setSelectedTxs] = useState<string[]>([])
  const [selectedMatches, setSelectedMatches] = useState<string[]>([])
  const [openMatch, setOpenMatch] = useState<string | null>(null)

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['reconciliation'] })
    void queryClient.invalidateQueries({ queryKey: ['orders'] })
    void queryClient.invalidateQueries({ queryKey: ['transactions'] })
    void queryClient.invalidateQueries({ queryKey: ['overview'] })
  }

  const run = useMutation({
    mutationFn: runReconciliation,
    onSuccess: invalidate,
  })
  const confirm = useMutation({
    mutationFn: confirmMatch,
    onSuccess: invalidate,
  })
  const reject = useMutation({
    mutationFn: rejectMatch,
    onSuccess: invalidate,
  })
  const bulkConfirm = useMutation({
    mutationFn: bulkConfirmMatches,
    onSuccess: () => {
      setSelectedMatches([])
      invalidate()
    },
  })
  const bulkReject = useMutation({
    mutationFn: bulkRejectMatches,
    onSuccess: () => {
      setSelectedMatches([])
      invalidate()
    },
  })
  const manual = useMutation({
    mutationFn: () => confirmManualMatch(selectedOrders, selectedTxs),
    onSuccess: () => {
      setSelectedOrders([])
      setSelectedTxs([])
      invalidate()
    },
  })
  const resolve = useMutation({
    mutationFn: (id: string) => resolveException(id),
    onSuccess: invalidate,
  })

  const data = board.data
  const canManual = selectedOrders.length > 0 && selectedTxs.length > 0

  const candidatesByOrder = useMemo(() => {
    const map = new Map<string, CandidateOut[]>()
    for (const row of data?.candidates ?? []) {
      const list = map.get(row.order_id) ?? []
      list.push(row)
      map.set(row.order_id, list)
    }
    return map
  }, [data?.candidates])

  return (
    <div className="px-6 py-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">{t('rec.title')}</h1>
          <p className="mt-1 text-sm text-slate-500">{t('rec.lead')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Stat label={t('rec.auto')} value={String(data?.auto_matched ?? '—')} />
          <Stat label={t('rec.queue')} value={String(data?.pending_review ?? '—')} warn={(data?.pending_review ?? 0) > 0} />
          <Stat label={t('rec.gaps')} value={String(data?.open_exceptions ?? '—')} warn={(data?.open_exceptions ?? 0) > 0} />
          <button
            type="button"
            className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-900 disabled:opacity-50"
            disabled={run.isPending}
            onClick={() => run.mutate()}
          >
            {run.isPending ? t('rec.running') : t('rec.run')}
          </button>
        </div>
      </div>

      {run.data && (
        <p className="mt-3 text-sm text-slate-400">
          {t('rec.result', {
            a: run.data.auto_matched,
            p: run.data.proposed,
            c: run.data.candidates,
            e: run.data.exceptions,
          })}
        </p>
      )}
      {run.isError && (
        <p className="mt-3 text-sm text-loss">{run.error instanceof Error ? run.error.message : t('common.error')}</p>
      )}
      {manual.isError && (
        <p className="mt-3 text-sm text-loss">{manual.error instanceof Error ? manual.error.message : t('common.error')}</p>
      )}

      {(data?.exceptions.length ?? 0) > 0 && (
        <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-4">
          <h2 className="text-sm font-medium text-slate-200">{t('rec.gaps')}</h2>
          <ul className="mt-3 space-y-2">
            {data?.exceptions.map((row) => (
              <li key={row.id} className="flex items-start justify-between gap-3 text-sm">
                <div>
                  <div className={SEVERITY_CLASS[row.severity] ?? 'text-slate-200'}>{row.title}</div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {row.kind}
                    {row.impact_amount
                      ? ` · ${formatMoney(row.impact_amount, row.impact_currency ?? undefined)}`
                      : ''}
                    {' · '}
                    {formatDateTime(row.detected_at)}
                  </div>
                </div>
                <button
                  type="button"
                  className="shrink-0 text-xs text-slate-400 hover:text-slate-200"
                  onClick={() => resolve.mutate(row.id)}
                >
                  {t('rec.close')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {board.isLoading && <p className="mt-8 text-sm text-slate-500">{t('rec.loading')}</p>}
      {board.isError && (
        <p className="mt-8 text-sm text-loss">
          {board.error instanceof Error ? board.error.message : t('rec.openFail')}
        </p>
      )}

      {data && (
        <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
          <Column
            title={t('rec.noMoney')}
            count={data.unmatched_orders.length}
            action={
              canManual ? (
                <button
                  type="button"
                  className="text-xs text-slate-200 hover:text-white"
                  onClick={() => manual.mutate()}
                  disabled={manual.isPending}
                >
                  {t('rec.manual')}
                </button>
              ) : null
            }
          >
            {data.unmatched_orders.length === 0 && (
              <p className="text-sm text-slate-500">{t('rec.noOpen')}</p>
            )}
            {data.unmatched_orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                selected={selectedOrders.includes(order.id)}
                extraCandidates={candidatesByOrder.get(order.id)?.length ?? 0}
                onToggle={() => setSelectedOrders(toggle(selectedOrders, order.id))}
              />
            ))}
          </Column>

          <Column
            title={t('rec.reviewCol')}
            count={data.proposed.length}
            action={
              selectedMatches.length > 0 ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="text-xs text-slate-200 hover:text-white"
                    onClick={() => bulkConfirm.mutate(selectedMatches)}
                  >
                    {t('rec.confirmN', { n: selectedMatches.length })}
                  </button>
                  <button
                    type="button"
                    className="text-xs text-slate-400 hover:text-slate-200"
                    onClick={() => bulkReject.mutate(selectedMatches)}
                  >
                    {t('rec.reject')}
                  </button>
                </div>
              ) : null
            }
          >
            {data.proposed.length === 0 && (
              <p className="text-sm text-slate-500">{t('rec.noBelow')}</p>
            )}
            {data.proposed.map((match) => (
              <MatchCard
                key={match.id}
                match={match}
                selected={selectedMatches.includes(match.id)}
                open={openMatch === match.id}
                busy={confirm.isPending || reject.isPending}
                onToggleSelect={() => setSelectedMatches(toggle(selectedMatches, match.id))}
                onToggleOpen={() => setOpenMatch(openMatch === match.id ? null : match.id)}
                onConfirm={() => confirm.mutate(match.id)}
                onReject={() => reject.mutate(match.id)}
              />
            ))}
          </Column>

          <Column title={t('rec.noTxCol')} count={data.unmatched_transactions.length}>
            {data.unmatched_transactions.length === 0 && (
              <p className="text-sm text-slate-500">{t('rec.noTx')}</p>
            )}
            {data.unmatched_transactions.map((tx) => (
              <TxCard
                key={tx.id}
                tx={tx}
                selected={selectedTxs.includes(tx.id)}
                onToggle={() => setSelectedTxs(toggle(selectedTxs, tx.id))}
              />
            ))}
          </Column>
        </div>
      )}

      {data && data.confirmed.length > 0 && (
        <section className="mt-8">
          <h2 className="text-sm font-medium text-slate-400">{t('rec.confirmed')}</h2>
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            {data.confirmed.map((match) => (
              <div key={match.id} className="rounded-md border border-surface-border px-3 py-2 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-slate-300">{typeLabel(match.match_type)}</span>
                  <span className="num text-xs text-slate-500">{formatScore(match.score)}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {match.orders.map((order) => order.counterparty ?? order.external_order_id ?? t('rec.deal')).join(', ')}
                  {' → '}
                  {match.transactions.map((tx) => tx.payer_hint ?? tx.account_name ?? t('rec.tx')).join(', ')}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function Column({
  title,
  count,
  action,
  children,
}: {
  title: string
  count: number
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="flex min-h-[28rem] flex-col rounded-lg border border-surface-border bg-surface-raised">
      <header className="flex items-center justify-between gap-2 border-b border-surface-border px-3 py-2">
        <div className="text-sm font-medium text-slate-200">
          {title} <span className="num text-slate-500">{count}</span>
        </div>
        {action}
      </header>
      <div className="flex-1 space-y-2 overflow-auto p-3">{children}</div>
    </section>
  )
}

function OrderCard({
  order,
  selected,
  extraCandidates,
  onToggle,
}: {
  order: ReconOrderOut
  selected: boolean
  extraCandidates: number
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={[
        'w-full rounded-md border px-3 py-2 text-left text-sm',
        selected ? 'border-slate-400 bg-slate-700/40' : 'border-surface-border hover:bg-slate-800/40',
      ].join(' ')}
    >
      <div className="flex justify-between gap-2">
        <span className="text-slate-200">{sideLabel(order.side)}</span>
        <span className="num text-slate-100">{formatMoney(order.fiat_amount, order.fiat_code)}</span>
      </div>
      <div className="mt-1 text-xs text-slate-500">
        {order.counterparty ?? t('rec.noNick')} · {order.exchange_code}
        {order.bank_name ? ` · ${order.bank_name}` : ''}
      </div>
      <div className="mt-0.5 text-xs text-slate-600">{formatDateTime(order.occurred_at)}</div>
      {extraCandidates > 0 && (
        <div className="mt-1 text-[11px] text-pending">{t('rec.candidates', { n: extraCandidates })}</div>
      )}
    </button>
  )
}

function TxCard({ tx, selected, onToggle }: { tx: ReconTxOut; selected: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={[
        'w-full rounded-md border px-3 py-2 text-left text-sm',
        selected ? 'border-slate-400 bg-slate-700/40' : 'border-surface-border hover:bg-slate-800/40',
      ].join(' ')}
    >
      <div className="flex justify-between gap-2">
        <span className="text-slate-200">{tx.direction === 'in' ? t('rec.in') : t('rec.out')}</span>
        <span className="num text-slate-100">{formatMoney(tx.amount, tx.currency_code)}</span>
      </div>
      <div className="mt-1 text-xs text-slate-500">
        {tx.payer_hint ?? t('rec.payerHidden')} · {tx.account_name ?? t('rec.account')}
      </div>
      <div className="mt-0.5 text-xs text-slate-600">{formatDateTime(tx.booked_at)}</div>
    </button>
  )
}

function MatchCard({
  match,
  selected,
  open,
  busy,
  onToggleSelect,
  onToggleOpen,
  onConfirm,
  onReject,
}: {
  match: MatchOut
  selected: boolean
  open: boolean
  busy: boolean
  onToggleSelect: () => void
  onToggleOpen: () => void
  onConfirm: () => void
  onReject: () => void
}) {
  const factors = (match.factors.factors ?? []) as ReconFactor[]
  return (
    <div
      className={[
        'rounded-md border px-3 py-2 text-sm',
        selected ? 'border-slate-400 bg-slate-700/30' : 'border-surface-border',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <label className="flex items-center gap-2 text-slate-200">
          <input type="checkbox" checked={selected} onChange={onToggleSelect} />
          <span>{typeLabel(match.match_type)}</span>
        </label>
        <span className="num text-slate-100">{formatScore(match.score)}</span>
      </div>
      <div className="mt-1 text-xs text-slate-400">
        {match.orders.map((order) => (
          <div key={order.id}>
            {sideLabel(order.side)} {formatMoney(order.fiat_amount, order.fiat_code)} ·{' '}
            {order.counterparty ?? order.external_order_id ?? t('rec.deal')}
          </div>
        ))}
        {match.transactions.map((tx) => (
          <div key={tx.id}>
            {tx.direction === 'in' ? '+' : '−'}
            {formatMoney(tx.amount, tx.currency_code)} · {tx.payer_hint ?? tx.account_name ?? t('rec.tx')}
          </div>
        ))}
      </div>
      {match.residual_explained_as === 'bank_fee' && (
        <div className="mt-1 text-[11px] text-pending">
          {t('rec.feeExpl', { v: formatMoney(match.residual_amount) })}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-900 disabled:opacity-50"
          disabled={busy}
          onClick={onConfirm}
        >
          {t('rec.confirm')}
        </button>
        <button
          type="button"
          className="rounded border border-surface-border px-2 py-1 text-xs text-slate-300 disabled:opacity-50"
          disabled={busy}
          onClick={onReject}
        >
          {t('rec.reject')}
        </button>
        <button type="button" className="text-xs text-slate-500 hover:text-slate-300" onClick={onToggleOpen}>
          {open ? t('rec.hideScore') : t('rec.whyScore')}
        </button>
      </div>
      {open && (
        <ul className="mt-2 space-y-1 text-[11px] text-slate-400">
          {factors.map((factor) => (
            <li key={factor.code}>
              <span className="text-slate-300">{factor.contribution}</span>
              {' · '}
              {factor.explanation}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="rounded-md border border-surface-border px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={['num text-sm', warn ? 'text-pending' : 'text-slate-100'].join(' ')}>{value}</div>
    </div>
  )
}

function toggle(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((item) => item !== id) : [...list, id]
}

function formatScore(value: string): string {
  const amount = Number(value)
  return Number.isFinite(amount) ? `${amount.toFixed(1)}` : value
}
